/**
 * VerificationModal - UI for device verification flows
 *
 * Handles both initiator and responder flows:
 * - Initiator: Starts verification using VerificationFlowController
 * - Responder: Accepts incoming verification request
 * - Uses reusable EmojiVerificationScreen component
 * - Supports both device and user verification
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import verificationFlowController, { VerificationState } from '../../crypto/verification/VerificationFlowController';
import useVerificationFlow, { VerificationFlowState } from '../../hooks/useVerificationFlow';
import EmojiVerificationScreen from './EmojiVerificationScreen';
import logger from '../../utils/logger';
import { useI18n } from '../../context/I18nContext';
import './VerificationModal.css';

/**
 * VerificationModal Component
 * 
 * @param {boolean} isOpen - Whether modal is open
 * @param {Function} onClose - Callback when modal closes
 * @param {Function} onComplete - Callback when verification completes (success boolean)
 * @param {Object} incomingRequest - Incoming verification request (responder flow)
 * @param {string} verificationType - Type of verification ('device' or 'user')
 * @param {string} displayName - Display name for context (device name or user name)
 */
const VerificationModal = ({
  isOpen,
  onClose,
  onComplete,
  incomingRequest = null,
  verificationType = 'device',
  displayName = null,
}) => {
  const { t } = useI18n();

  // Determine if this is initiator or responder flow
  const isResponder = !!incomingRequest;

  // State for initiator flow (using VerificationFlowController)
  // Initialize from singleton to prevent flash on remount - if modal remounts while
  // controller is in COMPLETE state, we start with COMPLETE instead of IDLE
  const [verificationState, setVerificationState] = useState(() => {
    return verificationFlowController.getCurrentState().state;
  });

  const [sasData, setSasData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [initiatorError, setInitiatorError] = useState(null);
  const [confirmingMatch, setConfirmingMatch] = useState(false);

  // State for responder flow (using useVerificationFlow hook)
  const {
    state: responderState,
    emojis: responderEmojis,
    error: responderError,
    acceptRequest,
    rejectRequest,
    confirmMatch,
    rejectMatch,
    cancel: cancelResponder,
  } = useVerificationFlow(incomingRequest, verificationType);

  // Unified state and data based on flow type
  const currentState = isResponder ? responderState : verificationState;
  const currentEmojis = useMemo(() => {
    const emojis = isResponder ? responderEmojis : sasData?.emoji;
    return emojis;
  }, [isResponder, responderEmojis, sasData]);
  const currentError = isResponder ? responderError : initiatorError;

  // Show accept/reject screen for incoming requests
  // Only show if request phase is still 'requested' (not yet accepted)
  const [showAcceptScreen, setShowAcceptScreen] = useState(
    isResponder && incomingRequest?.phase === 2,  // phase 2 = requested
  );

  const unsubscribeRef = useRef(null);
  const isMountedRef = useRef(true);
  const hasStartedVerificationRef = useRef(false);  // Prevent restart during completion
  
  // Store completion handlers in refs to avoid stale closures
  const completionHandlersRef = useRef({
    complete: null,
    cancelled: null,
  });

  // Handle verification complete
  const handleVerificationComplete = useCallback(() => {
    logger.info('VerificationModal: Verification complete');
    // Don't close automatically - let user see success screen and click OK
    if (!isMountedRef.current) return;
    // Note: onComplete and onClose will be called when user clicks OK button
  }, []);

  // Handle verification cancelled
  const handleVerificationCancelled = useCallback(() => {
    logger.info('VerificationModal: Verification cancelled');
    hasStartedVerificationRef.current = false;  // Allow retry after cancel
    onComplete?.(false);
    onClose();
  }, [onComplete, onClose]);

  // Update completion handlers ref when callbacks change
  completionHandlersRef.current = {
    complete: handleVerificationComplete,
    cancelled: handleVerificationCancelled,
  };

  // Start verification when modal opens (initiator flow only)
  // For responder flow, check if request was already accepted
  useEffect(() => {
    if (!isOpen) return;

    // Reset isMountedRef when modal opens - it was set to false by previous cleanup
    isMountedRef.current = true;

    // Check BOTH local state AND singleton controller state to prevent restart loop
    // This handles the case where modal remounts while controller is still in COMPLETE state
    const controllerState = verificationFlowController.getCurrentState().state;
    const terminalStates = [VerificationState.COMPLETE, VerificationState.CANCELLED, VerificationState.ERROR];

    // If controller is in terminal state, sync local state and don't restart
    if (terminalStates.includes(controllerState)) {
      setVerificationState(controllerState);
      return;
    }

    // Track if we should start verification - allows fresh start after reset
    let shouldStartVerification = false;

    // If controller is IDLE but local state is terminal, reset local state to allow fresh start
    // This handles the case where modal reopens after a previous cancelled/completed verification
    if (controllerState === VerificationState.IDLE && terminalStates.includes(verificationState)) {
      setVerificationState(VerificationState.IDLE);
      shouldStartVerification = true;  // Start verification after reset
    }

    // Don't start verification if we're in a terminal state (and controller is too)
    // Note: We check current state but don't add to dependencies to avoid re-running on state changes
    if (terminalStates.includes(verificationState) && terminalStates.includes(controllerState)) {
      return;
    }

    if (!isResponder && (verificationState === VerificationState.IDLE || shouldStartVerification)) {
      // Initiator flow: start verification
      startVerification();
    } else if (isResponder && incomingRequest) {
      // Responder flow: check if already accepted (phase > 2)
      logger.info('Opening verification modal as responder');
      if (incomingRequest.phase > 2) {
        setShowAcceptScreen(false);
      }
    }

    return () => {
      isMountedRef.current = false;
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [isOpen, isResponder, incomingRequest]);

  // Reset guard and controller when modal closes
  useEffect(() => {
    if (!isOpen) {
      hasStartedVerificationRef.current = false;
      // Reset controller LOCAL state only (don't send remote cancellation)
      // This cleanup runs whenever modal closes for any reason (cancel, error, or completion)
      // If cancellation was needed (e.g., "They don't match"), it was already sent by the action handler
      verificationFlowController.resetLocalState();
      // Clear any lingering error state
      setInitiatorError(null);
      setSasData(null);
    }
  }, [isOpen]);

  // Start verification flow
  const startVerification = useCallback(async () => {
    if (!isMountedRef.current) return;

    // Prevent multiple verification attempts in same session
    if (hasStartedVerificationRef.current) {
      return;
    }
    hasStartedVerificationRef.current = true;  // Mark as started
    // Don't set isLoading here - the REQUESTING state already shows a spinner
    // isLoading is only for the accept button in responder flow
    setInitiatorError(null);

    // Subscribe to state changes
    unsubscribeRef.current = verificationFlowController.onStateChange((newState) => {
      if (!isMountedRef.current) return;

      setVerificationState(newState);

      const currentData = verificationFlowController.getCurrentState();

      // CRITICAL: Only set sasData when emojis are actually present
      // This prevents race condition where sasData exists but emoji array is not yet populated
      if (currentData.sasData?.emoji) {
        setSasData(currentData.sasData);
        // No need to clear isLoading here - we don't set it in initiator flow
      }

      // Handle completion states using ref to avoid stale closures
      if (newState === VerificationState.COMPLETE) {
        completionHandlersRef.current.complete?.();
      } else if (newState === VerificationState.CANCELLED) {
        completionHandlersRef.current.cancelled?.();
      } else if (newState === VerificationState.ERROR) {
        setInitiatorError('VerificationModal.errorVerificationFailed');
      }
    });

    // CRITICAL: Check current state immediately after subscribing
    // to catch any state changes that happened during async setup
    const currentState = verificationFlowController.getCurrentState();
    if (currentState.state !== VerificationState.IDLE && currentState.state !== VerificationState.REQUESTING) {
      setVerificationState(currentState.state);
      // Only set sasData if emojis are present
      if (currentState.sasData?.emoji) {
        setSasData(currentState.sasData);
        // No need to clear isLoading - we don't set it in initiator flow
      }
    }

    try {
      const result = await verificationFlowController.startFallbackForSelf();
      if (!isMountedRef.current) return;

      logger.debug('VerificationModal: Verification result:', result);
    } catch (err) {
      if (!isMountedRef.current) return;
      
      logger.error('VerificationModal: Failed to start verification:', err);
      setInitiatorError('VerificationModal.errorFailedToStart');
    }
    // No finally block needed - isLoading not used in initiator flow
  }, [setVerificationState]);

  // Handle accept for incoming request (responder flow)
  const handleAccept = useCallback(async () => {
    if (!isMountedRef.current) return;
    
    setIsLoading(true);
    
    try {
      const success = await acceptRequest();
      
      if (!isMountedRef.current) return;
      
      if (success) {
        setShowAcceptScreen(false);
      } else {
        // acceptRequest failed, error already set by hook
        logger.warn('VerificationModal: Failed to accept request');
      }
    } catch (err) {
      if (!isMountedRef.current) return;
      
      logger.error('VerificationModal: Error accepting request:', err);
      // Error already set by the hook
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [acceptRequest]);

  // Handle reject for incoming request (responder flow)
  const handleReject = useCallback(async () => {
    try {
      await rejectRequest('user');
      
      if (!isMountedRef.current) return;
      
      onClose();
    } catch (err) {
      if (!isMountedRef.current) return;
      
      logger.error('VerificationModal: Error rejecting request:', err);
      // Still close the modal even if reject fails
      onClose();
    }
  }, [rejectRequest, onClose]);

  // Handle SAS confirmation
  const handleSasConfirm = useCallback(async () => {
    if (!isMountedRef.current) return;
    
    setConfirmingMatch(true);

    try {
      let success;
      if (isResponder) {
        success = await confirmMatch();
      } else {
        success = await verificationFlowController.confirmSasMatch();
      }
      
      if (!isMountedRef.current) return;
      
      if (!success && !isResponder) {
        setInitiatorError('VerificationModal.errorFailedToConfirm');
      }
    } catch (err) {
      if (!isMountedRef.current) return;
      
      logger.error('VerificationModal: Failed to confirm SAS:', err);
      if (!isResponder) {
        setInitiatorError('VerificationModal.errorFailedToConfirm');
      }
    } finally {
      if (isMountedRef.current) {
        setConfirmingMatch(false);
      }
    }
  }, [isResponder, confirmMatch]);

  // Handle SAS rejection
  const handleSasReject = useCallback(async () => {
    if (isResponder) {
      await rejectMatch();
    } else {
      verificationFlowController.cancel('mismatch');

      if (!isMountedRef.current) return;

      setInitiatorError('VerificationErrors.mismatchedSas');
    }
  }, [isResponder, rejectMatch, t]);

  // Cancel verification
  const handleCancel = useCallback(() => {
    if (isResponder) {
      cancelResponder('user');
    } else {
      verificationFlowController.cancel('user');
    }
    onClose();
  }, [isResponder, cancelResponder, onClose]);

  // Render accept/reject screen for incoming requests
  const renderAcceptScreen = () => {
    if (!isResponder) return null;

    return (
      <div className="verification-accept-screen">
        <div className="verification-accept-icon">
          <span>🔐</span>
        </div>
        <h3>{t('VerificationModal.newVerificationRequest', {}, 'New Verification Request')}</h3>
        <p className="verification-accept-message">
          {displayName ? (
            <>
              <strong>{displayName}</strong> {t('VerificationModal.wantsToVerify', {}, 'wants to verify')}
            </>
          ) : (
            <>
              {t('VerificationModal.aTypeWantsToVerify', { type: verificationType }, `A ${verificationType} wants to verify`)}
            </>
          )}
        </p>
        <p className="verification-accept-description">
          {t('VerificationModal.compareEmojis', {}, 'Compare emojis to confirm the connection is secure.')}
        </p>
        <div className="verification-accept-actions">
          <button
            className="btn btn-secondary"
            onClick={handleReject}
            disabled={isLoading}
          >
            {t('VerificationModal.reject', {}, 'Reject')}
          </button>
          <button
            className="btn btn-success"
            onClick={handleAccept}
            disabled={isLoading}
          >
            {isLoading ? t('VerificationModal.accepting', {}, 'Accepting...') : t('VerificationModal.accept', {}, 'Accept')}
          </button>
        </div>
      </div>
    );
  };

  // Render SAS emoji display using reusable component
  const renderSasEmojis = () => {
    if (!currentEmojis) {
      return null;
    }
    return (
      <EmojiVerificationScreen
        emojis={currentEmojis}
        onConfirm={handleSasConfirm}
        onReject={handleSasReject}
        isConfirming={confirmingMatch}
        contextText={displayName}
        verificationType={verificationType}
      />
    );
  };

  // Render content based on unified state
  const renderContent = () => {
    // Show accept/reject screen first for responder flow
    if (isResponder && showAcceptScreen) {
      return renderAcceptScreen();
    }

    // Map responder states to initiator states for unified rendering
    const mappedState = isResponder ? mapResponderState(responderState) : verificationState;

    switch (mappedState) {
      case VerificationState.IDLE:
      case VerificationState.REQUESTING:
        return (
          <div className="verification-loading">
            <div className="spinner" />
            <p>{t('VerificationModal.startingVerification', {}, 'Starting verification...')}</p>
          </div>
        );

      case VerificationState.READY:
        return (
          <div className="verification-ready">
            <p>{t('VerificationModal.waitingForOther', { type: verificationType }, `Waiting for the other ${verificationType} to respond...`)}</p>
            <div className="spinner" />
          </div>
        );

      case VerificationState.SHOW_SAS:
        return renderSasEmojis();

      case VerificationState.VERIFYING:
        return (
          <div className="verification-verifying">
            <div className="spinner" />
            <p>{t('VerificationModal.waitingForConfirmation', {}, 'Waiting for confirmation...')}</p>
          </div>
        );

      case VerificationState.COMPLETE:
        return (
          <div className="verification-complete">
            <div className="success-icon">✓</div>
            <h3>{t('VerificationModal.verificationComplete', {}, 'Verification Complete!')}</h3>
            <p>
              {verificationType === 'user'
                ? t('VerificationModal.userVerified', {}, 'The user is now verified.')
                : t('VerificationModal.deviceVerified', {}, 'Your device is now verified.')}
            </p>
          </div>
        );

      case VerificationState.CANCELLED:
        return (
          <div className="verification-cancelled">
            <div className="warning-icon">⚠️</div>
            <h3>{t('VerificationModal.verificationCancelled', {}, 'Verification Cancelled')}</h3>
            <p>{t('VerificationModal.verificationWasCancelled', {}, 'The verification was cancelled.')}</p>
          </div>
        );

      case VerificationState.ERROR:
        return (
          <div className="verification-error">
            <div className="error-icon">❌</div>
            <h3>{t('VerificationModal.verificationFailed', {}, 'Verification Failed')}</h3>
            <p>{currentError ? t(currentError, {}, currentError) : t('VerificationModal.errorOccurred', {}, 'An error occurred during verification.')}</p>
            {!isResponder && (
              <button className="btn btn-primary" onClick={startVerification}>
                {t('VerificationModal.tryAgain', {}, 'Try Again')}
              </button>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  // Map responder states to initiator states for unified rendering
  const mapResponderState = (state) => {
    switch (state) {
      case VerificationFlowState.IDLE:
        return VerificationState.IDLE;
      case VerificationFlowState.ACCEPTING:
        return VerificationState.REQUESTING;
      case VerificationFlowState.WAITING:
        return VerificationState.READY;
      case VerificationFlowState.SHOWING_SAS:
        return VerificationState.SHOW_SAS;
      case VerificationFlowState.VERIFYING:
        return VerificationState.VERIFYING;
      case VerificationFlowState.DONE:
        return VerificationState.COMPLETE;
      case VerificationFlowState.CANCELLED:
        return VerificationState.CANCELLED;
      case VerificationFlowState.ERROR:
        return VerificationState.ERROR;
      default:
        return VerificationState.IDLE;
    }
  };

  // Always render but hide when not open - this prevents unmount/remount during
  // React 18's concurrent rendering which causes visual flashing
  // Use Portal to render at document body level for proper z-index stacking
  return createPortal(
    <div className="verification-modal-overlay" style={{ display: isOpen ? 'flex' : 'none' }}>
      <div className="verification-modal">
        <div className="verification-modal-header">
          <h2>
            {verificationType === 'user'
              ? t('VerificationModal.verifyUser', {}, 'Verify User')
              : t('VerificationModal.verifyDevice', {}, 'Verify Device')}
          </h2>
          {currentState !== VerificationState.COMPLETE && (
            <button
              className="verification-modal-close"
              onClick={handleCancel}
              data-testid="verification-modal-close-btn"
            >
              ×
            </button>
          )}
        </div>

        <div className="verification-modal-content">
          {currentError && currentState !== VerificationState.ERROR && (
            <div className="verification-error-message">
              {t(currentError, {}, currentError)}
            </div>
          )}

          {renderContent()}
        </div>

        {(currentState === VerificationState.COMPLETE || currentState === VerificationFlowState.DONE) ? (
          <div className="verification-modal-footer">
            <button
              className="btn btn-primary"
              onClick={() => {
                // Reset singleton controller LOCAL state before closing to prevent loop on remount
                // Use resetLocalState() instead of reset() to avoid sending cancellation after success
                verificationFlowController.resetLocalState();
                onComplete?.(true);
                onClose();
              }}
            >
              {t('VerificationModal.ok', {}, 'OK')}
            </button>
          </div>
        ) : currentState !== VerificationState.CANCELLED &&
          currentState !== VerificationState.ERROR &&
          currentState !== VerificationFlowState.CANCELLED &&
          currentState !== VerificationFlowState.ERROR && (
          <div className="verification-modal-footer">
            <button className="btn btn-secondary" onClick={handleCancel}>
              {t('VerificationModal.cancel', {}, 'Cancel')}
            </button>
          </div>
        )}

        {isLoading && (
          <div className="verification-modal-loading">
            <div className="spinner" />
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

VerificationModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onComplete: PropTypes.func,
  incomingRequest: PropTypes.object,
  verificationType: PropTypes.oneOf(['device', 'user']),
  displayName: PropTypes.string,
};

export default VerificationModal;